import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ComicCard from "@/components/base/ComicCard";
import FilterChips from "@/components/base/FilterChips";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Icon from "@/components/base/Icon";
import { useSeo } from "@/hooks/useSeo";
import {
  allComics,
  filterComics,
  getCategoryById,
  getUniqueRegions,
  getUniqueYears,
} from "@/utils/comic";

const PAGE_SIZE = 8;

const STATUS_OPTIONS = [
  { label: "全部", value: "all" },
  { label: "连载中", value: "ongoing" },
  { label: "已完结", value: "completed" },
];

const SORT_OPTIONS = [
  { label: "按更新时间", value: "updated" },
  { label: "按人气", value: "views" },
  { label: "按评分", value: "rating" },
];

export default function CategoryPage() {
  const { categoryId = "all" } = useParams();
  const category = getCategoryById(categoryId);
  const isAll = categoryId === "all";

  const [status, setStatus] = useState("all");
  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);

  const regions = useMemo(
    () => [{ label: "全部", value: "all" }, ...getUniqueRegions().map((r) => ({ label: r, value: r }))],
    []
  );
  const years = useMemo(
    () => [
      { label: "全部", value: "all" },
      ...getUniqueYears().map((y) => ({ label: `${y} 年`, value: String(y) })),
    ],
    []
  );

  useEffect(() => {
    setStatus("all");
    setRegion("all");
    setYear("all");
    setSort("updated");
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [status, region, year, sort]);

  const list = useMemo(
    () =>
      filterComics({
        category: isAll ? "all" : categoryId,
        status,
        region,
        sort,
      }).filter((c) => (year === "all" ? true : String(c.year) === year)),
    [categoryId, isAll, status, region, year, sort]
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const title = isAll ? "全部漫画" : `${category?.name}漫画`;
  const description = isAll
    ? "羞羞色漫全部漫画作品列表，支持按地区、状态、年份筛选与排序。"
    : category?.desc || "";

  useSeo({
    title: `${title}－羞羞色漫在线阅读`,
    description: `${description} 羞羞色漫在线阅读平台，${title}分类共 ${list.length} 部作品。`,
    keywords: `${title},羞羞色漫,漫画阅读,在线漫画,${title}推荐`,
  });

  if (!isAll && !category) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <Icon name="ri-error-warning-line" className="w-12 h-12 text-foreground-400 mx-auto" />
        <p className="mt-4 text-foreground-700">未找到该分类</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "分类", to: "/category/all" },
          { label: title },
        ]}
      />

      <header className="mt-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {title}
        </h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
          {description} 共收录 <strong>{list.length}</strong> 部作品。
        </p>
      </header>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 flex flex-col gap-4">
        <FilterChips label="状态" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
        <div className="h-px bg-background-200" />
        <FilterChips label="地区" options={regions} value={region} onChange={setRegion} />
        <div className="h-px bg-background-200" />
        <FilterChips label="年份" options={years} value={year} onChange={setYear} />
        <div className="h-px bg-background-200" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <FilterChips label="排序" options={SORT_OPTIONS} value={sort} onChange={setSort} />
          <span className="text-xs text-foreground-400 whitespace-nowrap">
            筛选与排序为前端演示，实时刷新列表
          </span>
        </div>
      </section>

      {pageItems.length === 0 ? (
        <div className="mt-10 rounded-lg border border-background-200 bg-background-50 py-16 text-center">
          <Icon name="ri-inbox-line" className="w-12 h-12 text-foreground-300 mx-auto" />
          <p className="mt-4 text-foreground-700">当前筛选条件下暂无漫画</p>
          <button
            type="button"
            onClick={() => {
              setStatus("all");
              setRegion("all");
              setYear("all");
            }}
            className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {pageItems.map((comic) => (
            <ComicCard key={comic.id} comic={comic} showDesc />
          ))}
        </div>
      )}

      <Pagination current={currentPage} total={totalPages} onChange={setPage} />

      {isAll && (
        <p className="mt-6 text-center text-xs text-foreground-400">
          共 {allComics.length} 部演示作品，分页为前端模拟，后续可接入真实数据接口
        </p>
      )}
    </div>
  );
}